// Components/Pages/Admin/Dashboard.jsx
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  FaProjectDiagram, 
  FaBook, 
  FaHeart, 
  FaImages, 
  FaFilePdf, 
  FaVideo, 
  FaArrowRight,
  FaPlus,
  FaUpload
} from "react-icons/fa";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    checkAuth();
    fetchStats();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch('https://api.rigokenya.org/api/admin/me', {
        credentials: 'include'
      });
      if (!response.ok) {
        navigate('/admin/login');
        return;
      }
      const data = await response.json();
      setAdmin(data);
    } catch (error) {
      navigate('/admin/login');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('https://api.rigokenya.org/api/admin/stats', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Thematic Pillars', 
      count: stats?.total_pillars || 0, 
      icon: FaBook, 
      bgColor: 'bg-purple-50', 
      textColor: 'text-purple-600', 
      link: '/admin/pillars' 
    },
    { 
      title: 'Projects', 
      count: stats?.total_projects || 0, 
      icon: FaProjectDiagram, 
      bgColor: 'bg-blue-50', 
      textColor: 'text-blue-600', 
      link: '/admin/projects' 
    },
    { 
      title: 'Stories', 
      count: stats?.total_stories || 0, 
      icon: FaHeart, 
      bgColor: 'bg-pink-50', 
      textColor: 'text-pink-600', 
      link: '/admin/stories' 
    },
    { 
      title: 'Gallery', 
      count: stats?.total_gallery || 0, 
      icon: FaImages, 
      bgColor: 'bg-green-50', 
      textColor: 'text-green-600', 
      link: '/admin/gallery' 
    },
    { 
      title: 'Resources', 
      count: stats?.total_resources || 0, 
      icon: FaFilePdf, 
      bgColor: 'bg-red-50', 
      textColor: 'text-red-600', 
      link: '/admin/resources' 
    },
    { 
      title: 'Media', 
      count: stats?.total_media || 0, 
      icon: FaVideo, 
      bgColor: 'bg-yellow-50', 
      textColor: 'text-yellow-600', 
      link: '/admin/media' 
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primaryGold mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link
              key={index}
              to={stat.link}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 hover:transform hover:-translate-y-1"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-primaryBlue mt-1">{stat.count}</p>
                </div>
                <div className={`${stat.bgColor} p-3 rounded-xl`}>
                  <Icon className={`text-2xl ${stat.textColor}`} />
                </div>
              </div>
              <div className="mt-4 text-xs text-primaryGold flex items-center">
                Manage <FaArrowRight className="ml-1" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <Link to="/admin/pillars/add" className="bg-white rounded-xl shadow-md p-4 border border-gray-100 hover:shadow-lg transition-all">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg text-purple-600"><FaPlus /></div>
            <div><p className="font-medium text-sm">Add Thematic Pillar</p></div>
          </div>
        </Link>
        <Link to="/admin/projects/add" className="bg-white rounded-xl shadow-md p-4 border border-gray-100 hover:shadow-lg transition-all">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg text-blue-600"><FaPlus /></div>
            <div><p className="font-medium text-sm">Add Project</p></div>
          </div>
        </Link>
        <Link to="/admin/stories/add" className="bg-white rounded-xl shadow-md p-4 border border-gray-100 hover:shadow-lg transition-all">
          <div className="flex items-center space-x-3">
            <div className="bg-pink-100 p-2 rounded-lg text-pink-600"><FaPlus /></div>
            <div><p className="font-medium text-sm">Add Story</p></div>
          </div>
        </Link>
        <Link to="/admin/gallery/upload" className="bg-white rounded-xl shadow-md p-4 border border-gray-100 hover:shadow-lg transition-all">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg text-green-600"><FaUpload /></div>
            <div><p className="font-medium text-sm">Upload Image</p></div>
          </div>
        </Link>
        <Link to="/admin/resources/add" className="bg-white rounded-xl shadow-md p-4 border border-gray-100 hover:shadow-lg transition-all">
          <div className="flex items-center space-x-3">
            <div className="bg-red-100 p-2 rounded-lg text-red-600"><FaPlus /></div>
            <div><p className="font-medium text-sm">Add Resource</p></div>
          </div>
        </Link>
        <Link to="/admin/media/add" className="bg-white rounded-xl shadow-md p-4 border border-gray-100 hover:shadow-lg transition-all">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg text-yellow-600"><FaPlus /></div>
            <div><p className="font-medium text-sm">Add Video</p></div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;